import React from "react"
import PropTypes from "prop-types"
import { connect } from "react-redux"
import { bindActionCreators } from "redux"
import { useDidMount } from "rooks"
import { updateOrganization, saveOrganization } from "@api/settings/organization"
import { getTeamMembers } from "@api/settings/team"
import { getCountries } from "@api/utilities/"
import Select from "@src/components/select/"
import Logo from "./logo"
import Links from "./links"
import ExternalAuth from "./external_auth"
import { can, VIEW_USERS_AND_ROLES, MODIFY_SETTINGS } from "@src/helpers/authorization"

const OPENID_CONNECT = [
  "openidconnect",
  "microsoftentra"
]

const Form = ({ getCountries, countries, getTeamMembers, teamList, organization, user, updateOrganization, saveOrganization }) => {
  const canViewUsers = can(user, [VIEW_USERS_AND_ROLES])
  const canModify = can(user, [MODIFY_SETTINGS])
  useDidMount(() => {
    getCountries()
    // Only needed to transfer ownership.
    if (canViewUsers) {
      getTeamMembers()
    }
  })
  return (
    // Disabling the fieldset makes every native input read-only for users who can only view settings
    <fieldset disabled={!canModify} style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}>
      <div className='box-shadow rounded p2 bg-white'>
        <h4 className='mb2'>{organization.name} Profile</h4>
        <div>
          <div className='flex items-center border-bottom border-gray-5 pb2 mb2'>
            <Logo/>
            <div className='pl2 flex-auto'>
              <label className='cs-label' htmlFor='organization-name'>Organization Name</label>
              <input
                id='organization-name'
                value={organization.name}
                className={"cs-input col-12 block " + (!organization.name ? "error" : "")}
                onChange={e => updateOrganization({ name: e.target.value })}
                onBlur={e => {
                  if (e.target.value) {
                    saveOrganization("name", e.target.value)
                  }
                }} />
              {!organization.name && <div className='red'>Organization name is required</div>}
              <label htmlFor='organization-about' className='mt2 cs-label'>
                About
              </label>
              <textarea
                id='organization-about'
                value={organization.about || ""}
                className='cs-textarea col-12 block'
                onChange={e => updateOrganization({ about: e.target.value })}
                onBlur={e => saveOrganization("about", e.target.value)} />
            </div>
          </div>
          <div className='flex flex-wrap items-center'>
            <div className='col-4 pr2'>
              <label className='cs-label' htmlFor='organization-owner'>Owner</label>
              <Select
                id='organization-owner'
                isDisabled={organization.owner_id !== user._id || !canViewUsers || !canModify}
                options={teamList}
                isLoading={canViewUsers && !teamList.length}
                value={teamList.find(({ value }) => value === organization.owner_id)}
                clearable={false}
                className='col-12 mr2'
                onChange={user => {
                  updateOrganization({ owner_id: user.value })
                  saveOrganization("owner_id", user.value)
                }} />
            </div>
            <div className='col-4 pr2'>
              <label className='cs-label' htmlFor='organization-city'>City</label>
              <input
                id='organization-city'
                value={organization.city || ""}
                className='cs-input col-12 block'
                onChange={e => updateOrganization({ city: e.target.value })}
                onBlur={e => saveOrganization("city", e.target.value)} />
            </div>
            <div className='col-4'>
              <label className='cs-label' htmlFor='organization-country'>Country</label>
              <Select
                id='organization-country'
                options={countries.list}
                isLoading={countries.status !== "READY"}
                isDisabled={!canModify}
                value={countries.list.find(({ country_code }) => country_code === organization.country_code)}
                clearable={false}
                className='col-12 mr2'
                onChange={country => {
                  updateOrganization({ country_code: country.value })
                  saveOrganization("country_code", country.value)
                }} />
            </div>
            <div className='col-4 pr2'>
              <label className='cs-label mt2' htmlFor='organization-address-line1'>Address Line 1</label>
              <input
                id='organization-address-line1'
                value={organization.address_line1 || ""}
                className='cs-input col-12 block'
                onChange={e => updateOrganization({ address_line1: e.target.value })}
                onBlur={e => saveOrganization("address_line1", e.target.value)} />
            </div>
            <div className='col-4 pr2'>
              <label className='cs-label mt2' htmlFor='organization-address-line2'>Address Line 2</label>
              <input
                id='organization-address-line2'
                value={organization.address_line2 || ""}
                className='cs-input col-12 block'
                onChange={e => updateOrganization({ address_line2: e.target.value })}
                onBlur={e => saveOrganization("address_line2", e.target.value)} />
            </div>
            <div className='col-4'>
              <label className='cs-label mt2' htmlFor='organization-postal-code'>Postal Code</label>
              <input
                id='organization-postal-code'
                value={organization.postal_code || ""}
                className='cs-input col-12 block'
                onChange={e => updateOrganization({ postal_code: e.target.value })}
                onBlur={e => saveOrganization("postal_code", e.target.value)} />
            </div>
          </div>
        </div>
      </div>
      <Links />
      {OPENID_CONNECT.includes(organization.external_authentication_type) && <ExternalAuth />}
    </fieldset>
  )
}

Form.propTypes = {
  organization: PropTypes.shape({
    name: PropTypes.string.isRequired
  }).isRequired,
  updateOrganization: PropTypes.func.isRequired,
  saveOrganization: PropTypes.func.isRequired,
  getCountries: PropTypes.func.isRequired,
  countries: PropTypes.shape({
    status: PropTypes.string.isRequired,
    list: PropTypes.array.isRequired
  }).isRequired,
  getTeamMembers: PropTypes.func.isRequired,
  teamList: PropTypes.array.isRequired
}

const mapStateToProps = state => ({
  user: state.user,
  organization: state.settings.organization,
  countries: state.utilities.countries,
  teamList: state.settings.team.list.map(user => ({ label: user.email, value: user._id }))
})

const mapDispatchToProps = dispatch => ({
  updateOrganization: bindActionCreators(updateOrganization, dispatch),
  saveOrganization: bindActionCreators(saveOrganization, dispatch),
  getCountries: bindActionCreators(getCountries, dispatch),
  getTeamMembers: bindActionCreators(getTeamMembers, dispatch)
})

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Form)
